import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { Subject, filter, takeUntil } from 'rxjs';

/**
 * Interface representing a breadcrumb navigation item.
 */
export interface Breadcrumb {
  /** Display text for the breadcrumb link */
  label: string;
  /** URL path for navigation when breadcrumb is clicked */
  url: string;
}

/**
 * Breadcrumb navigation component that dynamically generates breadcrumb trails.
 *
 * @remarks Automatically creates breadcrumb navigation based on the current route.
 * Provides contextual navigation showing the user's current location within the application.
 * Hides breadcrumbs on home pages and provides friendly labels for common routes.
 */
@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, SvgIconComponent],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  /** Array of breadcrumb items for the current navigation path */
  breadcrumbs: Breadcrumb[] = [];
  /** Flag controlling breadcrumb visibility */
  showBreadcrumbs = false;
  /** Subject for managing component cleanup */
  private destroy$ = new Subject<void>();

  /**
   * Creates an instance of BreadcrumbComponent.
   *
   * @param router - Angular router service for navigation events
   */
  constructor(private router: Router) {}

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Sets up router event subscription to update breadcrumbs
   * on navigation changes and initializes breadcrumbs for current route.
   */
  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.createBreadcrumbs();
      });

    // Initialize breadcrumbs on component init
    this.createBreadcrumbs();
  }

  /**
   * Component cleanup lifecycle hook.
   *
   * @remarks Completes the destroy subject to clean up subscriptions
   * and prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Creates breadcrumb navigation items based on the current route.
   *
   * @remarks Analyzes the current URL path and generates appropriate
   * breadcrumb items with user-friendly labels. Hides breadcrumbs
   * on home pages and provides special handling for common routes.
   */
  private createBreadcrumbs(): void {
    const url = this.router.url;

    // Hide breadcrumbs on home page
    if (url === '/' || url === '/privatkunden') {
      this.showBreadcrumbs = false;
      return;
    }

    this.showBreadcrumbs = true;

    // Always start with Symbox as first breadcrumb
    this.breadcrumbs = [
      {
        label: 'Symbox',
        url: '/',
      },
    ];

    // Get the route path after the first '/'
    const path = url.substring(1); // remove leading slash

    if (path) {
      // Map routes to more user-friendly labels
      let label: string;

      switch (path) {
        case 'downloads':
          label = 'Downloads';
          break;
        case 'contact':
          label = 'Kontakt';
          break;
        case 'imprint':
          label = 'Impressum';
          break;
        case 'datenschutz':
          label = 'Datenschutz';
          break;
        default:
          // Convert path to title case if no specific mapping is found
          label = path.charAt(0).toUpperCase() + path.slice(1);
      }

      this.breadcrumbs.push({
        label,
        url: `/${path}`,
      });
    }
  }
}
